import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";

const SupportTicketForm = () => {
  const [category, setCategory] = useState("Bug");
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState("");
  const [screenshotFile, setScreenshotFile] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (session?.user?.email) setUserEmail(session.user.email);
    };
    fetchUser();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.user?.id) return alert("User not authenticated");

    let screenshot_url = null;
    if (screenshotFile) {
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("support-screenshots")
        .upload(
          `tickets/${Date.now()}_${screenshotFile.name}`,
          screenshotFile
        );

      if (uploadError) {
        alert("Failed to upload screenshot: " + uploadError.message);
        return;
      }

      const { data: urlData } = supabase.storage
        .from("support-screenshots")
        .getPublicUrl(uploadData.path);

      screenshot_url = urlData.publicUrl;
    }

    const { error } = await supabase.from("support_tickets").insert({
      user_id: session.user.id,
      email: session.user.email,
      category,
      subject,
      description,
      status: "Open",
      screenshot_url,
    });

    if (error) alert("Failed to submit ticket: " + error.message);
    else {
      await supabase.from("admin_notifications").insert({
        type: "support_ticket",
        message: `New support ticket submitted by ${session.user.email}: ${subject}`,
        created_at: new Date().toISOString(),
      });
      navigate("/dashboard");
    }
  };

  return (
    <main className="mx-auto mt-8 max-w-xl rounded-2xl border border-white/10 bg-slate-900/95 px-5 py-6 text-slate-100 shadow-xl shadow-black/40 backdrop-blur">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="mb-4 inline-flex items-center gap-1 text-sm text-cyan-300 underline decoration-cyan-400/70 underline-offset-4 hover:text-cyan-200"
      >
        ← Back
      </button>

      <h2 className="text-center text-xl font-semibold text-sky-200 md:text-2xl">
        🙋 Support Request Form
      </h2>
      <p className="mt-2 text-center text-xs text-slate-400">
        Tell us what’s going wrong or what you’d like to improve. We’ll review
        and respond as soon as possible.
      </p>

      <form onSubmit={handleSubmit} className="mt-5 space-y-4 text-sm">
        {/* Category */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-200">
            Category
          </label>
          <select
            className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 focus:border-cyan-400 focus:outline-none"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            <option>Bug</option>
            <option>Feature Request</option>
            <option>Performance Issue</option>
            <option>Other</option>
          </select>
        </div>

        {/* Subject */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-200">
            Subject
          </label>
          <input
            type="text"
            className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            required
            placeholder="Short summary of your issue"
          />
        </div>

        {/* Description */}
        <div className="flex flex-col gap-1">
          <label className="text-xs font-semibold text-slate-200">
            Description
          </label>
          <textarea
            rows={5}
            className="resize-y rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            placeholder="Please include steps to reproduce, what you expected, and what actually happened."
          />
        </div>

        {/* Screenshot field (hidden, BUT still present) */}
        <div className="hidden">
          <label className="text-xs font-semibold text-slate-200">
            Screenshot (optional)
          </label>
          <input
            type="file"
            onChange={(e) => setScreenshotFile(e.target.files[0])}
            accept="image/*"
            className="mt-1 text-xs text-slate-300"
          />
        </div>

        {/* Hidden email field (unchanged logic, just hidden) */}
        <div className="hidden">
          <label className="text-xs font-semibold text-slate-200">Email</label>
          <input
            type="text"
            value={userEmail}
            disabled
            className="rounded-xl border border-slate-600 bg-slate-950/70 px-3 py-2 text-sm text-slate-100"
          />
        </div>

        {/* Buttons */}
        <div className="mt-4 flex justify-end gap-3 text-sm">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center rounded-xl border border-slate-600 bg-slate-900 px-4 py-2 font-semibold text-slate-100 hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="inline-flex items-center rounded-xl bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500 px-4 py-2 font-semibold text-white shadow-md shadow-fuchsia-500/40 hover:brightness-110"
          >
            Submit Ticket
          </button>
        </div>
      </form>
    </main>
  );
};

export default SupportTicketForm;
